/**
 * Table component with sorting and pagination
 */

import React from 'react';
import { clsx } from 'clsx';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
}

export interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  selectedRows?: Set<string>;
  getRowId?: (row: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  pagination?: boolean;
  pageSize?: number;
  rowClassName?: (row: T) => string;
}

export function Table<T>({
  data,
  columns,
  pagination = false,
  pageSize = 10,
  rowClassName,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  selectedRows,
  getRowId,
  isLoading = false,
  emptyMessage = 'No data available',
}: TableProps<T>) {
  const [currentPage, setCurrentPage] = React.useState(0);
  
  // Apply pagination
  const displayData = pagination 
    ? data.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    : data;
  
  const totalPages = pagination ? Math.ceil(data.length / pageSize) : 1;
  
  return (
    <div>
      <div className="overflow-x-auto border border-theme-border rounded-lg">
        <table className="min-w-full divide-y divide-theme-border">
        <thead className="bg-theme-surface">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={clsx(
                  'px-6 py-3 text-left text-xs font-medium text-theme-text-secondary uppercase tracking-wider',
                  column.sortable && 'cursor-pointer hover:bg-theme-surface-hover',
                  column.width
                )}
                onClick={() => column.sortable && onSort?.(column.key)}
              >
                <div className="flex items-center gap-1">
                  {column.header}
                  {column.sortable && (
                    <span className="ml-1">
                      {sortBy === column.key ? (
                        sortOrder === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-4 w-4 text-theme-text-secondary" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-theme-paper divide-y divide-theme-border">
          {isLoading ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center text-theme-text-secondary">
                <div className="flex justify-center">
                  <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center text-theme-text-secondary">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            displayData.map((row, idx) => {
              const rowId = getRowId ? getRowId(row) : String(idx);
              const isSelected = selectedRows?.has(rowId);
              const customClass = rowClassName ? rowClassName(row) : '';

              return (
                <tr
                  key={rowId}
                  className={clsx(
                    customClass,
                    onRowClick && 'cursor-pointer hover:bg-theme-surface-hover',
                    isSelected && 'bg-primary-50'
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-theme-text">
                      {column.render
                        ? column.render((row as any)[column.key], row)
                        : String((row as any)[column.key] || '-')}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>
      
      {/* Pagination Controls */}
      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-theme-border bg-theme-paper">
          <div className="text-sm text-theme-text">
            Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, data.length)} of {data.length} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 border border-theme-border rounded disabled:opacity-50 bg-theme-paper text-theme-text hover:bg-theme-surface-hover"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-theme-text">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-1 border border-theme-border rounded disabled:opacity-50 bg-theme-paper text-theme-text hover:bg-theme-surface-hover"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

